import { useEffect, useRef, useState } from 'react';

/**
 * A sitter shares their booking address constantly: in a text, an email, an
 * Instagram bio. Showing it is not enough, so this is the whole link plus one
 * button that puts it on the clipboard.
 *
 * The Clipboard API needs a secure context and a user gesture. Both hold here,
 * but a browser can still refuse, so a failure selects the text instead of
 * leaving the sitter with a button that silently does nothing.
 *
 * `url` is also used for a bare value that is copied but never visited, such
 * as the target of a DNS record. That is not an address to send anyone to, and
 * offered as one it resolves against the current page and lands on a dead
 * route, so Open appears only for a real link.
 */
export function CopyLink({ url, label }: { url: string; label?: string }) {
  const openable = /^https?:\/\//.test(url);
  const [state, setState] = useState<'idle' | 'copied' | 'select'>('idle');
  const fieldRef = useRef<HTMLInputElement>(null);
  const timer = useRef<number>();

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = () => {
    window.clearTimeout(timer.current);
    void (async () => {
      try {
        await navigator.clipboard.writeText(url);
        setState('copied');
        timer.current = window.setTimeout(() => setState('idle'), 2000);
      } catch {
        fieldRef.current?.select();
        setState('select');
      }
    })();
  };

  return (
    <div>
      {label && (
        <p className="mb-1.5 text-sm text-[color:var(--color-ink-soft)]">{label}</p>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {/* Read-only rather than plain text: it stays selectable, and the
            fallback path can focus and select it. min-w-0 keeps a long address
            from pushing the button off the row. */}
        <input
          ref={fieldRef}
          readOnly
          value={url}
          onFocus={(event) => event.currentTarget.select()}
          aria-label={label ?? (openable ? 'Booking link' : 'Value to copy')}
          className="min-w-0 flex-1 rounded border border-[color:var(--color-rule)] bg-[color:var(--color-sheet)] px-3 py-2 font-mono text-sm"
        />
        <button type="button" className="btn btn-secondary shrink-0" onClick={copy}>
          {state === 'copied' ? 'Copied' : 'Copy'}
        </button>
        {openable && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            className="btn btn-quiet shrink-0"
          >
            Open
          </a>
        )}
      </div>
      {state === 'select' && (
        <p className="mt-1.5 text-sm text-[color:var(--color-ink-soft)]">
          Your browser blocked the clipboard. The link is selected, so copy it now.
        </p>
      )}
    </div>
  );
}
